import { useEffect, useState } from 'react';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Input } from '../ui/input';
import { useAuth } from '../../context/AuthContext';
import { toast } from 'sonner';
import { ShieldCheck, AlertOctagon, AlertTriangle, PlayCircle, Calendar } from 'lucide-react';

/**
 * iter-221c — Admin Plan Management panel
 *
 * Lives on the Admin → Vendor Billing detail page. Shows the live trial-
 * gate verdict for the vendor (ok / trial_expired / suspended) plus a
 * single override action: "Start Professional Trial" with an optional
 * custom end-date. Used to rescue vendors stuck in Expired / Pending.
 */
const META = {
  ok: {
    label: 'On track',
    cls: 'bg-emerald-50 text-emerald-700 border-emerald-200',
    Icon: ShieldCheck,
  },
  trial_expired: {
    label: 'Trial expired',
    cls: 'bg-amber-50 text-amber-700 border-amber-200',
    Icon: AlertTriangle,
  },
  suspended: {
    label: 'Suspended',
    cls: 'bg-rose-50 text-rose-700 border-rose-200',
    Icon: AlertOctagon,
  },
};

export default function PlanManagementPanel({ vendorId, onAction }) {
  const { apiCall } = useAuth();
  const [verdict, setVerdict] = useState(null);
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState(false);
  const [customEnd, setCustomEnd] = useState('');

  const fetchVerdict = async () => {
    if (!vendorId) return;
    setLoading(true);
    try {
      const data = await apiCall(`/api/vendor-subscription/admin/trial-gate-status/${vendorId}`);
      setVerdict(data);
    } catch (e) {
      // silent — admin tooling
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchVerdict(); }, [vendorId]); // eslint-disable-line

  const startTrial = async (reason = 'admin_manual_trial') => {
    if (running) return;
    setRunning(true);
    try {
      const body = { reason };
      if (customEnd) body.trial_end = `${customEnd}T23:59:59+00:00`;
      const out = await apiCall(
        `/api/vendor-subscription/admin/start-trial/${vendorId}`,
        { method: 'POST', body: JSON.stringify(body) }
      );
      if (out.no_op) {
        toast.warning('Vendor already has an active or trial subscription — no change made');
      } else {
        toast.success(
          `Trial started — ends ${out.trial_end ? new Date(out.trial_end).toLocaleDateString('en-IN') : 'in 30 days'}`
        );
      }
      await fetchVerdict();
      if (onAction) onAction();
    } catch (e) {
      toast.error('Failed to start trial');
    } finally {
      setRunning(false);
    }
  };

  const m = META[verdict?.gate] || META.ok;
  const Icon = m.Icon;

  return (
    <Card className="border-indigo-100" data-testid="plan-management-panel">
      <CardContent className="pt-5">
        <div className="flex items-start justify-between flex-wrap gap-3 mb-4">
          <div className="flex items-center gap-2">
            <Icon className="h-5 w-5 text-indigo-500" />
            <div>
              <p className="text-sm font-semibold text-gray-800">Plan Management</p>
              <p className="text-xs text-gray-500">
                Admin override — start a trial, or check the gate status of this vendor.
              </p>
            </div>
          </div>
          {!loading && verdict && (
            <Badge className={`${m.cls} border capitalize`} data-testid={`plan-mgmt-status-${verdict.gate}`}>
              {m.label}
              {typeof verdict.trial_days_remaining === 'number' && (
                <span className="ml-1 font-mono">· {verdict.trial_days_remaining}d left</span>
              )}
            </Badge>
          )}
        </div>

        {!loading && verdict && verdict.reason && (
          <p className="text-xs text-gray-500 mb-3 -mt-2">
            Reason: <span className="font-mono">{verdict.reason}</span>
            {verdict.days_overdue ? <> · overdue by {verdict.days_overdue}d</> : null}
          </p>
        )}

        <div className="flex flex-wrap items-end gap-3">
          <div>
            <label className="text-xs text-gray-500 block mb-1 flex items-center gap-1">
              <Calendar className="h-3 w-3" /> Custom trial end (optional)
            </label>
            <Input
              type="date"
              value={customEnd}
              onChange={(e) => setCustomEnd(e.target.value)}
              className="h-9 w-44"
              data-testid="plan-mgmt-custom-end-input"
            />
          </div>
          <Button
            onClick={() => startTrial(customEnd ? 'admin_custom_trial_end' : 'admin_manual_trial')}
            disabled={running}
            className="bg-indigo-600 hover:bg-indigo-700 h-9"
            data-testid="plan-mgmt-start-trial-btn"
          >
            <PlayCircle className={`h-4 w-4 mr-2 ${running ? 'animate-pulse' : ''}`} />
            {running ? 'Starting…' : 'Start Professional Trial'}
          </Button>
          <Button
            variant="outline"
            onClick={fetchVerdict}
            disabled={loading}
            className="h-9"
            data-testid="plan-mgmt-refresh-btn"
          >
            Refresh status
          </Button>
        </div>

        <p className="text-[11px] text-gray-400 mt-3">
          Idempotent: running on a vendor with an active sub is a no-op.
          The action is logged to the admin audit log.
        </p>
      </CardContent>
    </Card>
  );
}
